import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["videoContainer", "downloadButton", "browserButton"]
  static values = { 
    activeBrowser: String,
    chromeUrl: String,
    edgeUrl: String,
    safariUrl: String
  }

  // Browser-specific video URLs and download links
  browserConfig = {
    chrome: {
      videoId: "6a00e6407e827b05a8aeb2c061406f18", // Replace with actual Chrome video ID
      buttonText: "Download for Chrome"
    },
    edge: {
      videoId: "416a2548e4866d6802969e76245540cc", // Replace with actual Edge video ID
      buttonText: "Download for Edge"
    },
    safari: {
      videoId: "df1450d90854cc1e524e3470e4be2cdd", // Replace with actual Safari video ID
      buttonText: "Download for Safari"
    }
  }

  connect() {
    // Get the modal element
    this.modal = document.getElementById('browser_plugin_modal')
    
    // Add event listeners for modal open/close
    this.modal.addEventListener('showModal', this.handleModalOpen.bind(this))
    this.modal.addEventListener('close', this.handleModalClose.bind(this))
    
    // Initialize all buttons with proper styling
    this.browserButtonTargets.forEach(button => {
      button.classList.add('border', 'border-transparent')
    })

    // Detect current browser
    this.detectedBrowser = this.detectBrowser()
    
    // Find the button for the detected browser
    const browserButton = this.browserButtonTargets.find(button => button.dataset.browser === this.detectedBrowser)
    
    // Only update button order if we found a matching browser button
    if (browserButton) {
      // Move the detected browser button to be first in the container
      const container = browserButton.parentElement
      container.insertBefore(browserButton, container.firstChild)
      
      this.activeBrowserValue = this.detectedBrowser
    }
  }
  
  handleModalOpen() {
    // Only load video when modal is opened
    if (this.activeBrowserValue) {
      this.updateUI(this.activeBrowserValue)
    } else if (this.detectedBrowser) {
      this.updateUI(this.detectedBrowser)
    }
  }

  handleModalClose() {
    // Clear video container and reset to placeholder
    this.videoContainerTarget.innerHTML = `
      <div style="position: relative; padding-top: 64.69907407407408%;">
        <div class="absolute inset-0 flex items-center justify-center text-base-content/50">
          <div class="text-center p-8">
            <iconify-icon icon="ph:browser" class="text-4xl mb-4"></iconify-icon>
            <p class="text-lg">Select a browser to see installation instructions</p>
          </div>
        </div>
      </div>
    `
    
    // Hide download button
    this.downloadButtonTarget.classList.add("hidden")
    
    // Reset browser button borders
    this.browserButtonTargets.forEach(button => {
      button.classList.remove('border-base-content')
      button.classList.add('border-transparent')
    })
  }

  detectBrowser() {
    const userAgent = navigator.userAgent.toLowerCase()
    
    // Detect Edge first as it also includes Chrome in UA
    if (userAgent.includes("edg/")) {
      return "edge"
    }
    // Detect Safari
    else if (userAgent.includes("safari/") && !userAgent.includes("chrome")) {
      return "safari"
    }
    // Default to Chrome (also catches Chrome-like browsers)
    else {
      return "chrome"
    }
  }

  selectBrowser(event) {
    const browser = event.currentTarget.dataset.browser
    this.activeBrowserValue = browser
    this.updateUI(browser)
  }

  getDownloadUrl(browser) {
    switch(browser) {
      case 'chrome':
        return this.chromeUrlValue
      case 'edge':
        return this.edgeUrlValue
      case 'safari':
        return this.safariUrlValue
      default:
        return ''
    }
  }

  updateUI(browser) {
    const config = this.browserConfig[browser]
    
    // Update browser button borders - ensure only one button has visible border
    this.browserButtonTargets.forEach(button => {
      if (button.dataset.browser === browser) {
        button.classList.remove('border-transparent')
        button.classList.add('border-base-content')
      } else {
        button.classList.remove('border-base-content')
        button.classList.add('border-transparent')
      }
    })
    
    // Update video container with Cloudflare iframe
    this.videoContainerTarget.innerHTML = `
      <div style="position: relative; padding-top: 64.69907407407408%;">
        <iframe
          src="https://customer-ei0uh1h7sgxy2b2l.cloudflarestream.com/${config.videoId}/iframe?poster=https%3A%2F%2Fcustomer-ei0uh1h7sgxy2b2l.cloudflarestream.com%2F${config.videoId}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
          loading="lazy"
          style="border: none; position: absolute; top: 0; left: 0; height: 100%; width: 100%;"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowfullscreen="true"
        ></iframe>
      </div>
    `
    
    // Update and show download button
    this.downloadButtonTarget.innerHTML = `
      <iconify-icon icon="ph:download-simple" class="mr-2"></iconify-icon>
      ${config.buttonText}
    `
    this.downloadButtonTarget.classList.remove("hidden")
    
    // Add click handler for the download button
    this.downloadButtonTarget.onclick = () => {
      window.open(this.getDownloadUrl(browser), '_blank')
    }
  }
} 